import { useState } from 'react';
import { cancelTask } from './tasks';
import { ENTITY_TYPE, useGenericBi } from './use-generic-bi';

/** The task the confirm is about — id for the write, title for the sentence it shows. */
export interface CancelTaskTarget {
  id: string;
  title: string;
}

export interface CancelTaskFlow {
  /** The task awaiting confirmation, or null when no confirm is up. */
  target: CancelTaskTarget | null;
  busy: boolean;
  /** The cancel failed; the still-open confirm says so. */
  error: boolean;
  /** The trigger — a menu item or a detail button — asks for the confirm. */
  request: (target: CancelTaskTarget) => void;
  /** The user backed out. */
  dismiss: () => void;
  /** The user confirmed. Resolves true once the task is cancelled. */
  confirm: () => Promise<boolean>;
}

/**
 * The cancel-a-task flow, shared by every surface that offers it (the task-detail drill-in and the list
 * row's overflow menu) so the confirm step, the BI and the optimistic write can't drift between them.
 *
 * `page` is the src:172 page descriptor of whichever screen the trigger sits on — `TASKS_PAGE` for the
 * list row, `TASK_DETAILS_PAGE` for the drill-in — so a click reports the screen it happened on while
 * the `button_name` / `componentName` vocabulary stays identical across both (and, deliberately,
 * identical to the literals both native apps send).
 */
export function useCancelTask(page: { page_name: string; page_version: string }): CancelTaskFlow {
  const { logPageClickBi } = useGenericBi();
  const [target, setTarget] = useState<CancelTaskTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const bi = (buttonName: string, taskId: string) =>
    logPageClickBi(
      { ...page, button_name: buttonName, task_id: taskId },
      { entity: { id: taskId, type: ENTITY_TYPE.task }, componentName: buttonName.replace(/_/g, '-') },
    );

  return {
    target,
    busy,
    error,
    request: (next) => {
      bi('cancel_task', next.id);
      setError(false);
      setTarget(next);
    },
    dismiss: () => {
      setTarget(null);
      setError(false);
    },
    confirm: async () => {
      if (!target || busy) return false;
      bi('confirm_cancel_task', target.id);
      setBusy(true);
      setError(false);
      const result = await cancelTask(target.id);
      setBusy(false);
      if (!result.ok) {
        // Keep the confirm open saying it failed — closing it would leave the user with a task that
        // still says In progress and no account of why the action they took did nothing.
        setError(true);
        return false;
      }
      setTarget(null);
      return true;
    },
  };
}
